/**
 * Homeowner Members Service
 * 
 * Handles operations for homeowner members (members)
 */

import { supabase, SUPABASE_URL, ensureSession } from './client';
import { SESSION_MISSING_CODE } from './sessionGatedFetch';

/**
 * A missing/expired session, not an answer about the user: 42501 (anon role
 * denied), PGRST301 (JWT rejected) or the session gate's local 401. These must
 * NEVER become default data — React Query would cache it as SUCCESS under a
 * user-less key (['homeownerContext'], ['propertyAccess', id]) and e.g. give a
 * view-only investor owner rights (#2912). Fail closed: rethrow.
 */
export function isAuthFailure(error: { code?: string } | null | undefined): boolean {
  return (
    !!error &&
    (error.code === '42501' || error.code === 'PGRST301' || error.code === SESSION_MISSING_CODE)
  );
}

/**
 * Member access level (issue #2912).
 * - 'editor'  = full household write access (legacy co-owner behaviour)
 * - 'viewer'  = read-only account handed to investors / Allianz
 */
export type MemberAccessLevel = 'editor' | 'viewer';

export async function getHomeownerContext() {
  // Wait for the restored session before any membership/ownership query so this
  // never races auth on cold load and mis-resolves the user as a non-owner (#2907).
  const session = await ensureSession();
  const user = session?.user;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  // Does the user OWN any property? An owner is always "primary" and can edit
  // their own house — even if they were ALSO invited as a member elsewhere.
  const { data: owned, error: ownedError } = await supabase
    .from('home_owner_objects')
    .select('id')
    .eq('owner_id', user.id)
    .limit(1);
  if (isAuthFailure(ownedError)) throw ownedError;
  const ownsProperty = (owned?.length ?? 0) > 0;

  // ALL active memberships — a user can be a member of several households, so
  // this must NOT use .maybeSingle() (that errors on >1 row).
  let { data, error } = await supabase
    .from('homeowner_members')
    .select('primary_homeowner_id, status, access_level')
    .eq('member_user_id', user.id)
    .eq('status', 'active');

  if (isAuthFailure(error)) throw error;

  // Resilience: fall back to legacy columns if `access_level` isn't present yet.
  if (error) {
    const legacy = await supabase
      .from('homeowner_members')
      .select('primary_homeowner_id, status')
      .eq('member_user_id', user.id)
      .eq('status', 'active');
    data = legacy.data as typeof data;
    error = legacy.error;
  }

  if (error) {
    if (isAuthFailure(error)) throw error;
    console.error('Error checking homeowner membership:', error);
    // Fail safe (NON-auth errors only, e.g. a legacy schema): assume owner-like
    // defaults so a real owner is never locked out.
    return { user, primaryHomeownerId: user.id, isPrimary: true, accessLevel: 'editor' as MemberAccessLevel, canEdit: true };
  }

  const memberships = (data || []) as unknown as Array<{ primary_homeowner_id: string; access_level?: string | null }>;
  const isMemberSomewhere = memberships.length > 0;

  // A "pure viewer" (investor/Allianz) owns NO property and every membership is
  // view-only. Only a pure viewer is read-only; an owner or an editor-member can
  // edit. This is what stops a homeowner losing edit rights on their own house
  // after being invited (as viewer) to someone else's house.
  const isPureViewer =
    !ownsProperty && isMemberSomewhere && memberships.every((m) => m.access_level === 'viewer');

  // Owner (or a plain user with no membership) is primary of their own household;
  // a pure member belongs to the household that invited them.
  const isPrimary = ownsProperty || !isMemberSomewhere;

  return {
    user,
    primaryHomeownerId: isPrimary ? user.id : memberships[0].primary_homeowner_id,
    isPrimary,
    accessLevel: (isPureViewer ? 'viewer' : 'editor') as MemberAccessLevel,
    canEdit: !isPureViewer,
  };
}

export type PropertyRole = 'owner' | 'editor' | 'viewer' | 'none';

/**
 * Per-property access for the current user. Owner and member are PER-PROPERTY
 * roles — a user can OWN one house and be a view-only member of another — so
 * house-data edit surfaces must gate on the SELECTED property's access, not a
 * global flag. (RLS enforces the same per-property rule server-side.)
 */
export async function getPropertyAccess(
  propertyId: string,
): Promise<{ role: PropertyRole; isOwner: boolean; canEdit: boolean }> {
  const session = await ensureSession();
  const user = session?.user;
  if (!user || !propertyId) return { role: 'none', isOwner: false, canEdit: false };

  // Owner of THIS property → full access.
  const { data: prop, error: propError } = await supabase
    .from('home_owner_objects')
    .select('owner_id')
    .eq('id', propertyId)
    .maybeSingle();
  // An auth failure is not "no such property": rethrow instead of caching 'none'.
  if (isAuthFailure(propError)) throw propError;
  const ownerId = (prop as { owner_id?: string } | null)?.owner_id;
  if (ownerId && ownerId === user.id) return { role: 'owner', isOwner: true, canEdit: true };
  if (!ownerId) return { role: 'none', isOwner: false, canEdit: false };

  // Otherwise, their membership in THIS property's household decides.
  let { data: m, error } = await supabase
    .from('homeowner_members')
    .select('access_level')
    .eq('member_user_id', user.id)
    .eq('primary_homeowner_id', ownerId)
    .eq('status', 'active')
    .maybeSingle();
  if (error) {
    // Fallback if access_level column isn't present yet (legacy = editor).
    const legacy = await supabase
      .from('homeowner_members')
      .select('status')
      .eq('member_user_id', user.id)
      .eq('primary_homeowner_id', ownerId)
      .eq('status', 'active')
      .maybeSingle();
    m = legacy.data as typeof m;
  }
  const membership = m as { access_level?: string | null } | null;
  if (membership) {
    const isViewer = membership.access_level === 'viewer';
    return { role: isViewer ? 'viewer' : 'editor', isOwner: false, canEdit: !isViewer };
  }
  // Can see it (RLS) but no direct membership row → treat as view-only.
  return { role: 'viewer', isOwner: false, canEdit: false };
}

export interface HomeownerMember {
  id: string;
  primary_homeowner_id: string;
  member_user_id: string;
  status: 'pending' | 'active' | 'removed';
  access_level: MemberAccessLevel;
  invited_at: string;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  member_profile?: {
    id: string;
    email: string;
    full_name: string | null;
  };
}

export interface HomeownerMemberInvitation {
  id: string;
  primary_homeowner_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  token: string;
  status: 'pending' | 'accepted' | 'expired';
  access_level: MemberAccessLevel;
  expires_at: string;
  created_at: string;
}

export interface InviteMemberInput {
  email: string;
  firstName: string;
  lastName: string;
  /** Defaults to 'viewer' (read-only investor account) if omitted. */
  accessLevel?: MemberAccessLevel;
  /** Property ids to grant the member. Omitted/empty => all owner properties. */
  propertyIds?: string[];
}

export const homeownerMembersService = {
  /**
   * Get all members for the current homeowner
   */
  async getMembers(): Promise<HomeownerMember[]> {
    const { primaryHomeownerId } = await getHomeownerContext();

    const { data, error } = await supabase
      .from('homeowner_members')
      .select(`
        *,
        member_profile:profiles!homeowner_members_member_user_id_fkey (
          id,
          email,
          full_name
        )
      `)
      .eq('primary_homeowner_id', primaryHomeownerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching members:', error);
      throw new Error(`Failed to fetch members: ${error.message}`);
    }

    return (data || []).map((item: any) => ({
      ...item,
      member_profile: item.member_profile?.[0] || item.member_profile,
    })) as HomeownerMember[];
  },

  /**
   * Get all pending invitations for the current homeowner
   */
  async getInvitations(): Promise<HomeownerMemberInvitation[]> {
    const { primaryHomeownerId, isPrimary } = await getHomeownerContext();

    if (!isPrimary) {
      return [];
    }

    const { data, error } = await supabase
      .from('homeowner_member_invitations')
      .select('*')
      .eq('primary_homeowner_id', primaryHomeownerId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching invitations:', error);
      throw new Error(`Failed to fetch invitations: ${error.message}`);
    }

    return (data || []) as HomeownerMemberInvitation[];
  },

  /**
   * Invite a new member
   */
  async inviteMember(input: InviteMemberInput): Promise<{ success: boolean; invitation_id?: string; member_id?: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User must be authenticated');
    }

    const { isPrimary } = await getHomeownerContext();
    if (!isPrimary) {
      throw new Error('Only the primary homeowner can invite members');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/invite-homeowner-member`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(input),
    });

    const result = await response.json();

    if (!response.ok || result.error) {
      throw new Error(result.error || 'Failed to send invitation');
    }

    return result;
  },

  /**
   * Remove a member (completely deletes from all tables including auth.users)
   */
  async removeMember(memberId: string): Promise<void> {
    const { primaryHomeownerId, isPrimary } = await getHomeownerContext();

    if (!isPrimary) {
      throw new Error('Only the primary homeowner can remove members');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('User must be authenticated');
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/remove-homeowner-member`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ memberId }),
    });

    const result = await response.json();

    if (!response.ok || result.error) {
      throw new Error(result.error || 'Failed to remove member');
    }
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitationId: string): Promise<void> {
    const { primaryHomeownerId, isPrimary } = await getHomeownerContext();

    if (!isPrimary) {
      throw new Error('Only the primary homeowner can cancel invitations');
    }

    const { error } = await supabase
      .from('homeowner_member_invitations')
      .delete()
      .eq('id', invitationId)
      .eq('primary_homeowner_id', primaryHomeownerId);

    if (error) {
      console.error('Error canceling invitation:', error);
      throw new Error(`Failed to cancel invitation: ${error.message}`);
    }
  },

  /**
   * Resend invitation email
   */
  async resendInvitation(invitationId: string): Promise<void> {
    // For now, we'll need to get the invitation and call inviteMember again
    // In the future, we could add a separate endpoint for resending
    const { primaryHomeownerId, isPrimary } = await getHomeownerContext();

    if (!isPrimary) {
      throw new Error('Only the primary homeowner can resend invitations');
    }

    const { data: invitation, error: fetchError } = await supabase
      .from('homeowner_member_invitations')
      .select('*')
      .eq('id', invitationId)
      .eq('primary_homeowner_id', primaryHomeownerId)
      .single();

    if (fetchError || !invitation) {
      throw new Error('Invitation not found');
    }

    // Delete old invitation and create new one
    await this.cancelInvitation(invitationId);
    await this.inviteMember({
      email: invitation.email,
      firstName: invitation.first_name || '',
      lastName: invitation.last_name || '',
      // access_level column may not be in generated types yet (type-lag).
      accessLevel: (invitation as { access_level?: MemberAccessLevel }).access_level || 'viewer',
    });
  },

  /**
   * Determine if the current user is the primary homeowner (not an invited member)
   */
  async isPrimaryHomeowner(): Promise<boolean> {
    const { isPrimary } = await getHomeownerContext();
    return isPrimary;
  },

  /**
   * Get the primary homeowner ID for the current user's household
   */
  async getPrimaryHomeownerId(): Promise<string> {
    const { primaryHomeownerId } = await getHomeownerContext();
    return primaryHomeownerId;
  },

  /**
   * Whether the current user OWNS at least one property. This is the accurate
   * definition of "can invite members" — unlike isPrimaryHomeowner() (which is
   * account-global and returns false if the user is a member of ANY household),
   * this correctly stays true for a real homeowner even if their account also
   * appears as a member somewhere. Used to gate the owner-only Members nav.
   */
  async ownsAnyProperty(): Promise<boolean> {
    // Wait for the restored session first — otherwise this races auth on cold
    // load, runs anon, returns 0 rows, and the owner's controls get hidden (#2907).
    const session = await ensureSession();
    const user = session?.user;
    if (!user) return false;

    const { data, error } = await supabase
      .from('home_owner_objects')
      .select('id')
      .eq('owner_id', user.id)
      .limit(1);

    if (error) {
      console.error('Error checking property ownership:', error);
      // Fail safe: don't hide owner controls on a transient error.
      return true;
    }
    return (data?.length ?? 0) > 0;
  },

  /**
   * Pending invitations addressed to the CURRENT logged-in user (by email).
   * Used to show existing users an in-app "accept invitation" prompt instead of
   * re-sending a password-setup email. RLS ("Invitees can view their own
   * invitations") restricts this to the user's own email.
   */
  async getMyPendingInvitations(): Promise<HomeownerMemberInvitation[]> {
    const session = await ensureSession();
    const email = session?.user?.email;
    if (!email) return [];

    const { data, error } = await supabase
      .from('homeowner_member_invitations')
      .select('*')
      .ilike('email', email)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching my pending invitations:', error);
      return [];
    }
    return (data || []) as unknown as HomeownerMemberInvitation[];
  },

  /**
   * Accept an invitation as an EXISTING user (creates membership + property
   * assignments via the accept-homeowner-invitation edge function; no password).
   */
  async acceptInvitation(token: string): Promise<{ success: boolean }> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('User must be authenticated');

    const response = await fetch(`${SUPABASE_URL}/functions/v1/accept-homeowner-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ token }),
    });

    const result = await response.json();
    if (!response.ok || result.error) {
      throw new Error(result.error || 'Failed to accept invitation');
    }
    return result;
  },
};

